import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Target, 
  Eye, 
  Heart, 
  Users, 
  Award, 
  Rocket,
  CheckCircle2,
  Lightbulb,
  Shield,
  Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export const About = () => {
  const values = [
    {
      icon: Lightbulb,
      title: 'Innovation First',
      description: 'We constantly push boundaries to deliver cutting-edge solutions that keep you ahead of the competition.',
      color: 'from-yellow-400 to-orange-500',
    },
    {
      icon: Shield,
      title: 'Trust & Reliability',
      description: 'We build lasting relationships through transparency, integrity, and consistent delivery of quality work.',
      color: 'from-blue-500 to-indigo-600',
    },
    {
      icon: Users,
      title: 'Client Partnership',
      description: 'We see ourselves as an extension of your team, working collaboratively to achieve your vision.',
      color: 'from-purple-500 to-pink-600',
    },
    {
      icon: Zap,
      title: 'Excellence',
      description: 'We are committed to delivering exceptional results that exceed expectations every time.',
      color: 'from-teal-500 to-cyan-600',
    },
  ];

  const milestones = [
    { year: '2017', title: 'Company Founded', description: 'Started with a vision to democratize AI technology.' },
    { year: '2018', title: 'First Major Client', description: 'Partnered with Fortune 500 company for AI integration.' },
    { year: '2023', title: 'Setu Connect Launch', description: 'Released our flagship Shopify-NetSuite integration platform.' },
    { year: '2024', title: '50+ Clients', description: 'Expanded our client base across multiple industries.' },
    { year: '2025', title: 'Global Expansion', description: 'Opened offices in 3 new countries.' },
    { year: '2026', title: '15+ Projects', description: 'Delivered over 150 successful projects worldwide.' },
  ];

  const team = [
    { name: 'Alex Thompson', role: 'CEO & Founder', avatar: 'AT', color: 'from-blue-500 to-indigo-600' },
    { name: 'Sarah Chen', role: 'CTO', avatar: 'SC', color: 'from-purple-500 to-pink-600' },
    { name: 'Michael Roberts', role: 'Head of AI', avatar: 'MR', color: 'from-orange-500 to-red-600' },
    { name: 'Emily Davis', role: 'Lead Developer', avatar: 'ED', color: 'from-teal-500 to-cyan-600' },
  ];

  return (
    <div data-testid="about-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 hero-gradient mesh-gradient">
        <div className="absolute top-20 right-10 w-72 h-72 bg-purple-400/20 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
                About Us
              </span>
              <h1 
                data-testid="about-hero-title"
                className="text-4xl md:text-6xl font-bold font-['Outfit'] tracking-tighter mb-6"
              >
                Pioneering the Future of{' '}
                <span className="gradient-text">AI Innovation</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                We are a team of passionate technologists, developers, and innovators dedicated to 
                transforming businesses through intelligent AI solutions and cutting-edge development.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button 
                    data-testid="about-cta-button"
                    size="lg"
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full px-8 py-6 font-semibold shadow-lg shadow-blue-500/25 group"
                  >
                    Work With Us
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758691737387-a89bb8adf768?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB0ZWNoJTIwb2ZmaWNlJTIwdGVhbSUyMGNvbGxhYm9yYXRpb258ZW58MHx8fHwxNzcxODU3MTQ2fDA&ixlib=rb-4.1.0&q=85"
                alt="Team Collaboration"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section data-testid="mission-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {/* Mission */}
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-0 rounded-2xl overflow-hidden">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold font-['Outfit'] mb-4">Our Mission</h3>
                <p className="text-slate-600 leading-relaxed">
                  To empower businesses with intelligent AI solutions and seamless integrations 
                  that drive growth, efficiency, and competitive advantage.
                </p>
              </CardContent>
            </Card>

            {/* Vision */}
            <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-0 rounded-2xl overflow-hidden">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mb-6">
                  <Eye className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold font-['Outfit'] mb-4">Our Vision</h3>
                <p className="text-slate-600 leading-relaxed">
                  To be the global leader in AI-powered business transformation, making 
                  advanced technology accessible to organizations of all sizes.
                </p>
              </CardContent>
            </Card>

            {/* Values */}
            <Card className="bg-gradient-to-br from-teal-50 to-cyan-50 border-0 rounded-2xl overflow-hidden">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center mb-6">
                  <Heart className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold font-['Outfit'] mb-4">Our Values</h3>
                <p className="text-slate-600 leading-relaxed">
                  Innovation, integrity, and client success drive everything we do. 
                  We believe in building lasting partnerships through exceptional work.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Core Values Grid */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight">
              What We <span className="gradient-text">Stand For</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card 
                key={index}
                data-testid={`value-card-${index}`}
                className="group bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-lg hover:border-blue-100 transition-all card-hover"
              >
                <CardContent className="p-0">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${value.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <value.icon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-lg font-bold font-['Outfit'] mb-2">{value.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section data-testid="story-section" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
                Our Story
              </span>
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-6">
                From Vision to <span className="gradient-text">Reality</span>
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  JnanIT was founded in 2019 with a simple yet powerful vision: to make 
                  artificial intelligence accessible and actionable for businesses of all sizes.
                </p>
                <p>
                  What started as a small team of AI enthusiasts has grown into a full-service 
                  technology company delivering innovative solutions to clients worldwide. Our 
                  flagship product, Setu AI, emerged from our deep understanding of the challenges 
                  businesses face in connecting disparate systems.
                </p>
                <p>
                  Today, we're proud to have delivered over 150 successful projects, helping 
                  businesses automate processes, integrate systems, and unlock the power of AI. 
                  Our commitment to excellence and innovation drives everything we do.
                </p>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1618544976420-1f213fcf2052?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjB0ZWNoJTIwb2ZmaWNlJTIwdGVhbSUyMGNvbGxhYm9yYXRpb258ZW58MHx8fHwxNzcxODU3MTQ2fDA&ixlib=rb-4.1.0&q=85"
                alt="Working Session"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Timeline / Milestones */}
      <section data-testid="milestones-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-purple-50 text-purple-600 text-sm font-medium mb-4">
              Our Journey
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight">
              Key <span className="gradient-text">Milestones</span>
            </h2>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-blue-500 via-purple-500 to-pink-500 hidden lg:block" />
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div 
                  key={index}
                  data-testid={`milestone-${index}`}
                  className={`flex items-center gap-8 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                    <Card className="inline-block bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow">
                      <CardContent className="p-0">
                        <span className="text-3xl font-bold font-['Outfit'] gradient-text">{milestone.year}</span>
                        <h4 className="text-xl font-bold font-['Outfit'] mt-2 mb-2">{milestone.title}</h4>
                        <p className="text-slate-600">{milestone.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <div className="hidden lg:flex w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 items-center justify-center flex-shrink-0 z-10">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  
                  <div className="flex-1 hidden lg:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      {/* <section data-testid="team-section" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
              Our Team
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
              Meet the <span className="gradient-text">Innovators</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our diverse team of experts brings together decades of experience in AI, development, and business strategy.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <Card 
                key={index}
                data-testid={`team-member-${index}`}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 hover:shadow-xl transition-all group"
              >
                <CardContent className="p-0">
                  <div className={`h-48 bg-gradient-to-br ${member.color} flex items-center justify-center`}>
                    <span className="text-6xl font-bold text-white/90 font-['Outfit']">{member.avatar}</span>
                  </div>
                  <div className="p-6 text-center">
                    <h4 className="text-xl font-bold font-['Outfit'] mb-1">{member.name}</h4>
                    <p className="text-slate-600">{member.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-24 cta-gradient relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight text-white mb-6">
            Ready to Join Our Journey?
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Let's work together to build something extraordinary. Reach out to start your project.
          </p>
          <Link to="/contact">
            <Button 
              data-testid="about-cta-bottom"
              size="lg"
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-semibold shadow-xl group"
            >
              Get Started Today
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
